/* ==========================================================================
   <background-video>
   Attaches sources and plays only when doing so is worth it to the visitor.
   The poster underneath is always the fallback, never a blank rectangle.
   ========================================================================== */

(function () {
  'use strict';

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function connectionIsCheap() {
    const c = navigator.connection;
    if (!c) return true;
    if (c.saveData) return false;
    if (c.effectiveType && /^(slow-)?2g$/.test(c.effectiveType)) return false;
    return true;
  }

  class BackgroundVideo extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('video');
      if (!this.video) return;

      // A visitor who asked their system for less motion has asked for exactly
      // this. The poster stays; nothing is downloaded.
      if (reduceMotion.matches || !connectionIsCheap()) {
        this.setAttribute('data-state', 'poster');
        return;
      }

      this.loaded = false;

      if (this.hasAttribute('data-eager')) {
        this.load();
        this.watch();
        return;
      }

      if (!('IntersectionObserver' in window)) {
        this.load();
        return;
      }

      this.watch();
    }

    watch() {
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this.load();
              this.play();
            } else {
              this.pause();
            }
          });
        },
        { rootMargin: '200px 0px' }
      );
      this.observer.observe(this);
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }

    load() {
      if (this.loaded) return;
      this.loaded = true;

      // Sources live in <template> until now, so nothing is fetched before this.
      this.querySelectorAll('template[data-src]').forEach((tpl) => {
        const source = document.createElement('source');
        source.src = tpl.getAttribute('data-src');
        source.type = tpl.getAttribute('data-type');
        this.video.appendChild(source);
      });

      this.video.addEventListener(
        'playing',
        () => this.setAttribute('data-state', 'playing'),
        { once: true }
      );

      this.video.addEventListener('error', () => this.setAttribute('data-state', 'poster'), { once: true });

      this.video.load();
    }

    play() {
      const attempt = this.video.play();
      // Autoplay can still be refused. That is a valid outcome, not an error —
      // the poster is already doing the job.
      if (attempt && typeof attempt.catch === 'function') {
        attempt.catch(() => this.setAttribute('data-state', 'poster'));
      }
    }

    pause() {
      if (!this.video.paused) this.video.pause();
    }
  }

  customElements.get('background-video') || customElements.define('background-video', BackgroundVideo);

  // The tab going into the background should not keep frames decoding.
  document.addEventListener('visibilitychange', () => {
    document.querySelectorAll('background-video').forEach((el) => {
      if (document.hidden) {
        el.pause && el.pause();
      } else if (el.getAttribute('data-state') === 'playing') {
        el.play && el.play();
      }
    });
  });
})();
